import { IconIdentifier, SideCardType } from '@aus-platform/design-system';
import type { CustomSideBarOption } from 'src/map-3d/shared/map-3d-slices';
import { FeatureFlag } from 'src/shared/enums';
import {
  ComplianceAssessmentSidecard,
  GenerateDTM,
  Map3DBaseLayer,
  Map3DContour,
  Map3DElevationTool,
  Map3DHeapManagement,
  Map3DLegend,
  Map3DWorkspace,
} from './components';
import { AdditionalInformation } from './components/additional-information';
import { DrillAndBlast } from './components/drill-and-blast';
import { GenerateDEM } from './components/generate-dem';
import { HRASidecard } from './components/haul-road-analytics';
import { Map3DRoadAnalytics } from './components/map-3d-generate-analytics';
import { AnalyticsOutput } from './components/map-3d-generate-analytics/enums';
import { Map3DSlopeMap } from './components/map-3d-slope-map';
import { SmartDetect } from './components/smart-detect';
import { SmartDetectOutput } from './components/smart-detect/enums';
import { Map3DSidebarOption } from './shared/enums';
import { CrossSectionTool } from './components/cross-section/cross-section-tool';

// Contains different components for map-3d side-card
// which renders when iterated through sidebar.
export const map3dSidebarItems: CustomSideBarOption[] = [
  {
    icon: { identifier: IconIdentifier.LayerFilled, toolTipText: 'Workspace' },
    cardProps: {
      title: Map3DSidebarOption.Workspace,
      component: <Map3DWorkspace />,
    },
    isHidden: true,
    sidecardType: SideCardType.Custom,
  },
  {
    icon: {
      identifier: IconIdentifier.CrossSection,
      toolTipText: 'Cross Section',
    },
    cardProps: {
      title: Map3DSidebarOption.CrossSection,
      component: <CrossSectionTool />,
    },
    isHidden: true,
    sidecardType: SideCardType.Custom,
  },
  {
    icon: {
      identifier: IconIdentifier.Elevation,
      toolTipText: 'Elevation Profile',
    },
    cardProps: {
      title: Map3DSidebarOption.ElevationTool,
      component: <Map3DElevationTool />,
    },
    isHidden: true,
    sidecardType: SideCardType.Custom,
  },
  {
    icon: {
      identifier: IconIdentifier.Heap,
      toolTipText: 'Volume Calculation',
    },
    cardProps: {
      title: Map3DSidebarOption.VolumeCalculation,
      component: <Map3DHeapManagement />,
    },
    isHidden: true,
  },
  {
    icon: { identifier: IconIdentifier.Contour, toolTipText: 'Contour' },
    cardProps: {
      title: Map3DSidebarOption.Contour,
      component: <Map3DContour />,
    },
    isHidden: true,
  },
  {
    icon: { identifier: IconIdentifier.SlopeMap, toolTipText: 'Slope Map' },
    cardProps: {
      title: Map3DSidebarOption.SlopeMap,
      component: <Map3DSlopeMap />,
    },
    isHidden: true,
  },
  {
    icon: {
      identifier: IconIdentifier.Analytics,
      toolTipText: 'Generate Analytics',
    },
    cardProps: {
      title: Map3DSidebarOption.GenerateAnalytics,
      component: <Map3DRoadAnalytics />,
    },
    isHidden: true,
  },

  {
    icon: {
      identifier: IconIdentifier.Terrain,
      toolTipText: 'Generate DTM',
    },
    cardProps: {
      title: Map3DSidebarOption.GenerateDTM,
      component: <GenerateDTM />,
    },
    isHidden: true,
  },
  {
    icon: {
      // TODO: Update icon for DEM once we have a design for it.
      identifier: IconIdentifier.Iteration,
      toolTipText: 'Generate DEM',
    },
    cardProps: {
      title: Map3DSidebarOption.GenerateDEM,
      component: <GenerateDEM />,
    },
    isHidden: true,
  },

  {
    icon: {
      identifier: IconIdentifier.AnalyticsAi,
      toolTipText: 'Smart Detect',
    },
    cardProps: {
      title: Map3DSidebarOption.SmartDetect,
      component: <SmartDetect />,
    },
    isBeta: true,
    isHidden: true,
  },
  {
    icon: {
      identifier: IconIdentifier.RoadAnalytics,
      toolTipText: 'Haul Road Analytics',
    },
    cardProps: {
      title: Map3DSidebarOption.HaulRoadAnalytics,
      component: <HRASidecard />,
    },
    isBeta: true,
    isHidden: true,
    sidecardType: SideCardType.Custom,
  },
  {
    icon: {
      identifier: IconIdentifier.FactCheck,
      toolTipText: 'Compliance Assessment',
    },
    cardProps: {
      title: Map3DSidebarOption.ComplianceAssessment,
      component: <ComplianceAssessmentSidecard />,
    },
    isHidden: true,
  },
  {
    icon: {
      identifier: IconIdentifier.DrillAndBlast,
      toolTipText: 'Drill and Blast',
    },
    cardProps: {
      title: Map3DSidebarOption.DrillAndBlast,
      component: <DrillAndBlast />,
    },
    isBeta: true,
    isHidden: true,
  },
  {
    icon: {
      identifier: IconIdentifier.QuestionCircleFill,
      toolTipText: 'Legend',
    },
    cardProps: {
      title: Map3DSidebarOption.Legend,
      component: <Map3DLegend />,
    },
    isHidden: true,
  },
  {
    icon: {
      identifier: IconIdentifier.InfoCircle,
      toolTipText: 'Additional Information',
    },
    cardProps: {
      title: Map3DSidebarOption.AdditionalInformation,
      component: <AdditionalInformation />,
    },
    isHidden: true,
  },
  {
    icon: { identifier: IconIdentifier.Earth, toolTipText: 'Base Map' },
    cardProps: {
      title: Map3DSidebarOption.BaseMap,
      component: <Map3DBaseLayer />,
    },
    isOutsideSidecard: true,
    isHidden: true,
  },
];

export const smartDetectFeatureFlags = {
  [SmartDetectOutput.BenchToeCrest]: FeatureFlag.BenchToeCrestDetection,
  [SmartDetectOutput.BuildingFootprintsWithRoofTypes]:
    FeatureFlag.BuildingFootprintsOrRoofTopsDetection,
  [SmartDetectOutput.DrainageAnalysis]: FeatureFlag.DrainageAnalysis,
  [SmartDetectOutput.HeapDetection]: FeatureFlag.HeapDetection,
  [SmartDetectOutput.PondsAndLakes]: FeatureFlag.PondsAndLakesDetection,
  [SmartDetectOutput.RiversAndStreams]: FeatureFlag.RiversAndStreamsDetection,
  [SmartDetectOutput.TreeCanopies]: FeatureFlag.TreeCanopiesDetection,
  [SmartDetectOutput.MetalledRoads]: FeatureFlag.MetalledRoadsDetection,
  [SmartDetectOutput.UnMetalledRoads]: FeatureFlag.UnmetalledRoadsDetection,
  [SmartDetectOutput.BoreholeDetection]: FeatureFlag.BoreholeDetection,
  [SmartDetectOutput.FoundationPilingDetection]:
    FeatureFlag.FoundationPilingDetection,
  [SmartDetectOutput.PurlinDetection]: FeatureFlag.PurlinDetection,
  [SmartDetectOutput.SolarPanelDetection]: FeatureFlag.SolarPanelDetection,
  [SmartDetectOutput.DrillHoleDetection]: FeatureFlag.DrillHoleDetection,
  [SmartDetectOutput.VegetationCoverDetection]:
    FeatureFlag.VegetationCoverDetection,
};

// Feature flag mapping for Generate Analytics outputs
export const gaFeatureFlags = {
  [AnalyticsOutput.BenchAnalysis]:
    FeatureFlag.GenerateAnalyticsBenchAnalysisToeCrest,
  [AnalyticsOutput.HaulRoadGradient]:
    FeatureFlag.GenerateAnalyticsHaulRoadGradient,
  [AnalyticsOutput.HaulRoadWidth]: FeatureFlag.GenerateAnalyticsHaulRoadWidth,
  [AnalyticsOutput.DrainageAnalytics]:
    FeatureFlag.GenerateAnalyticsDrainageAnalytics,
  [AnalyticsOutput.SlopeStability]: FeatureFlag.GenerateAnalyticsSlopeStability,
  [AnalyticsOutput.LandUseLandCover]:
    FeatureFlag.GenerateAnalyticsLandUseLandCover,
  [AnalyticsOutput.SuperElevation]: FeatureFlag.GenerateAnalyticsSuperElevation,
};

// Any one of the defined feature flag should be enabled to show the sidebar option.
export const map3dSidebarFeatureFlags = {
  // Show GA if legacy flag is enabled OR any individual GA tool flag is enabled.
  [Map3DSidebarOption.GenerateAnalytics]: [
    FeatureFlag.GenerateAnalytics,
    FeatureFlag.GenerateAnalyticsBenchAnalysisToeCrest,
    FeatureFlag.GenerateAnalyticsHaulRoadGradient,
    FeatureFlag.GenerateAnalyticsHaulRoadWidth,
    FeatureFlag.GenerateAnalyticsDrainageAnalytics,
    FeatureFlag.GenerateAnalyticsSlopeStability,
    FeatureFlag.GenerateAnalyticsLandUseLandCover,
    FeatureFlag.GenerateAnalyticsSuperElevation,
  ],
  [Map3DSidebarOption.SlopeMap]: [FeatureFlag.SlopeMap],
  [Map3DSidebarOption.HaulRoadAnalytics]: [FeatureFlag.HaulRoadAnalytics],
  [Map3DSidebarOption.SmartDetect]: Object.values(smartDetectFeatureFlags),
  [Map3DSidebarOption.ComplianceAssessment]: [FeatureFlag.ComplianceAssessment],
  [Map3DSidebarOption.DrillAndBlast]: [FeatureFlag.DrillAndBlastRequest],
  [Map3DSidebarOption.GenerateDTM]: [FeatureFlag.GenerateDTMRequest],
  [Map3DSidebarOption.CrossSection]: [FeatureFlag.CrossSection],
  [Map3DSidebarOption.Contour]: [FeatureFlag.ContourGeneration],
  [Map3DSidebarOption.ElevationTool]: [FeatureFlag.ElevationProfile],
  [Map3DSidebarOption.VolumeCalculation]: [FeatureFlag.VolumeCalculation],
  [Map3DSidebarOption.GenerateDEM]: [FeatureFlag.GenerateDEM],
};
